import { toPrettyNumber, toPrettySpend } from '@src/utils/performance-utils';
import { Header } from '@Components/StickyTable/BPMTable';
import {
  BLENDED_LTV_BAN_MONTH_OFFSET,
  CohortLtvMatrixRow,
  DeliveryMetricsRowWithMetrics
} from './amc-ntb-ltv-types';

// The cohort LTV matrix goes out to the same month_offset the "Blended LTV" BAN reads (the
// matured-cohort cutoff) -- there's no independent reason for this table to stop anywhere else.
export const MAX_MONTH_OFFSET = BLENDED_LTV_BAN_MONTH_OFFSET;

const formatPercent = (value: number): string => `${(value * 100).toFixed(1)}%`;
const formatMoneyOrDash = (value: number | undefined): string =>
  value == null ? '—' : toPrettySpend(value, 2);

export const DELIVERY_METRICS_COHORT_TABLE_HEADERS: Header[] = [
  { name: 'cohortMonth', label: 'Cohort Month', flex: 1, minFlexWidth: 110 },
  {
    name: 'totalSpend',
    label: 'Total Spend',
    flex: 1,
    minFlexWidth: 110,
    renderer: (data: DeliveryMetricsRowWithMetrics) => toPrettySpend(data.totalSpend)
  },
  {
    name: 'ntbCustomers',
    label: 'NTB Customers',
    flex: 1,
    minFlexWidth: 120,
    renderer: (data: DeliveryMetricsRowWithMetrics) => toPrettyNumber(data.ntbCustomers)
  },
  {
    name: 'newToBrandPercent',
    label: 'New to Brand %',
    flex: 1,
    minFlexWidth: 120,
    renderer: (data: DeliveryMetricsRowWithMetrics) => formatPercent(data.newToBrandPercent)
  },
  {
    name: 'newToBrandOrderPercent',
    label: 'New to Brand Order %',
    flex: 1,
    minFlexWidth: 150,
    renderer: (data: DeliveryMetricsRowWithMetrics) => formatPercent(data.newToBrandOrderPercent)
  },
  {
    name: 'costPerNtbAcquisition',
    label: 'Cost per NTB Acquisition',
    flex: 1,
    minFlexWidth: 150,
    renderer: (data: DeliveryMetricsRowWithMetrics) => formatMoneyOrDash(data.costPerNtbAcquisition)
  },
  {
    name: 'ntbAverageOrderValue',
    label: 'NTB Average Order Value',
    flex: 1,
    minFlexWidth: 150,
    renderer: (data: DeliveryMetricsRowWithMetrics) => formatMoneyOrDash(data.ntbAverageOrderValue)
  }
];

export const COHORT_LTV_TABLE_HEADERS: Header[] = [
  { name: 'cohortMonth', label: 'Cohort Month', flex: 1, minFlexWidth: 110 },
  ...Array.from({ length: MAX_MONTH_OFFSET + 1 }, (_, monthOffset) => ({
    name: `month${monthOffset}`,
    label: String(monthOffset),
    flex: 1,
    minFlexWidth: 64,
    // No month{N} property exists on the row; BPMTable's default obj[name] sort would find
    // nothing to sort on without this.
    contentGetter: (data: CohortLtvMatrixRow) => data.valuesByMonthOffset[monthOffset] ?? 0,
    renderer: (data: CohortLtvMatrixRow) =>
      formatMoneyOrDash(data.valuesByMonthOffset[monthOffset] ?? undefined)
  }))
];
